/**
 * API base URL and endpoint paths for the TekkDev frontend.
 * Set REACT_APP_API_BASE_URL in .env (e.g. https://api.yourdomain.com or http://localhost:8000).
 * Full URL = base + path (e.g. POST ${base}/auth/login).
 */

const BASE =
  (process.env.REACT_APP_API_BASE_URL || "https://tekkdev.duckdns.org:3000").trim();
const VOICE_WS_OVERRIDE = (process.env.REACT_APP_VOICE_WS_URL || '').trim();

function hostnameLooksLikeNgrok(hostname) {
  if (!hostname) return false;
  return hostname.includes("ngrok");
}

export function getApiBase() {
  return BASE.replace(/\/$/, '');
}

/** WebSocket URL for a path (e.g. /ws/generate/voice_chat). Converts http(s) to ws(s). */
export function getWsUrl(path) {
  const base = getApiBase();
  const p = path.startsWith('/') ? path : `/${path}`;
  if (!base) {
    if (typeof window !== "undefined" && window.location?.host) {
      const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${wsProto}//${window.location.host}${p}`;
    }
    // Fallback for non-browser test/runtime environments.
    return `ws://localhost:3000${p}`;
  }
  const wsBase = base.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'wss://');
  return `${wsBase}${p}`;
}

/**
 * Voice WebSocket URL override.
 * If REACT_APP_VOICE_WS_URL is set, only voice WS uses that URL.
 */
export function getVoiceWsUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;

  let resolved;

  if (VOICE_WS_OVERRIDE) {
    const normalized = VOICE_WS_OVERRIDE
      .replace(/^http:\/\//i, 'ws://')
      .replace(/^https:\/\//i, 'wss://')
      .replace(/\/$/, '');

    // Full websocket endpoint provided (already includes /ws/...).
    if (/^wss?:\/\//i.test(normalized) && /\/ws\//i.test(normalized)) {
      resolved = normalized;
    } else {
      // Websocket base provided; append requested path.
      resolved = `${normalized}${p}`;
    }

    try {
      const apiHost = new URL(getApiBase()).hostname;
      const overrideVoiceHost = new URL(resolved).hostname;
      if (
        hostnameLooksLikeNgrok(overrideVoiceHost) &&
        !hostnameLooksLikeNgrok(apiHost) &&
        overrideVoiceHost !== apiHost
      ) {
        resolved = getWsUrl(path);
      }
    } catch (_) {
      /* keep resolved from override */
    }
  } else {
    resolved = getWsUrl(path);
  }

  return resolved;
}

// --- Health ---
export const HEALTH = '/health';
export const READY = '/ready';
export const HEALTH_DETAILED = '/health/detailed';

// --- Auth (/auth) ---
export const AUTH_SIGNUP = '/auth/signup';
export const AUTH_VERIFY_CODE = '/auth/verify-code';
export const AUTH_RESEND_CODE = '/auth/resend-code';
export const AUTH_LOGIN = '/auth/login';
export const AUTH_REFRESH = '/auth/refresh';
export const AUTH_ME = '/auth/me';
export const AUTH_FORGOT_PASSWORD = '/auth/forgot-password';
export const AUTH_CHANGE_PASSWORD = '/auth/change-password';
export const AUTH_SET_PASSWORD = '/auth/set-password';

// --- Chat / generate ---
export const GENERATE_CHAT = '/generate/chat';
export const GENERATE_CHAT_STREAM = '/generate/chat/stream';
export const GENERATE_COMPARATIVE_ANALYSIS = '/generate/comparative-analysis';
export const GENERATE_SUMMARY = '/generate/summary';
export const GENERATE_ANALYSIS = '/generate/analysis';
export const SESSION_CLEAR = '/session/clear';

// --- Documents ---
export const DOCUMENTS_UPLOAD = '/documents/upload';
export const DOCUMENTS_COMPARATIVE_ANALYSIS_UPLOAD = '/documents/comparative-analysis/upload';
export const DOCUMENTS_COMPARATIVE_ANALYSIS_CLEAR = '/documents/comparative-analysis/clear';
export const DOCUMENTS_COMPARATIVE_ANALYSIS_DOWNLOAD = '/documents/comparative-analysis/download';
export const DOCUMENTS_PROCESS_RAG = '/documents/process_rag';
export const DOCUMENTS_SEARCH_RAG = '/documents/search_rag';
export const DOCUMENTS_CLEAR_SESSION_RAG = '/documents/clear_session_rag';
export const DOCUMENTS_CLEAR_UPLOADS = '/documents/clear_uploads';

// --- WebSocket ---
// Live voice pipeline: STT + LLM streaming + TTS PCM chunks → Simli WebRTC.
export const WS_VOICE_STREAM = '/ws/voice/stream';
// Voice-only pipeline: STT + LLM + TTS WAV → local playback.
export const WS_VOICE_CHAT = '/ws/generate/voice_chat';

// --- Careers / admin ---
// Live backend exposes these paths without `/api` prefix.
export const API_JOBS = '/jobs';
export const API_APPLY = '/apply';
export const UPLOADS_BASE = '/uploads';
